import React from "react";
import styled, { useTheme } from "styled-components";
import { Direction, Signal } from "@shared/types";
import { ICON_TYPES, MARGIN_TYPE_TO_DISPLAY } from "@shared/common/constants";
import IconWithFallback from "Global/IconWithFallback";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { SignalFollowAlertIcon } from "features/signals/SignalFollow/SignalFollowAlertIcon";
import { translateOptionName } from "@shared/common/i18n/optionNames";

/**
 * The signals table's lead cell, as on the trades table and the dashboard:
 * the coin, the symbol over its side and leverage ("Short 5x Isolated").
 */
const NewLookSignalSymbol = ({ signal }: { signal: Signal }) => {
  const tokens = getNewLookTokens(useTheme());
  const extra = signal.signal_extra_data;
  const side = extra?.["Long/Short"];
  const marginType = extra?.["Margin Type"];
  const leverage = extra?.["Leverage"]
    ? [
        `${extra["Leverage"]}x`,
        marginType && translateOptionName(MARGIN_TYPE_TO_DISPLAY[marginType]),
      ]
        .filter(Boolean)
        .join(" ")
    : undefined;
  return (
    <Pair>
      <IconWithFallback
        coinOrExchangeName={signal.symbol}
        iconType={ICON_TYPES.COIN}
        pxIconSize={28}
        iconStyle={{ borderRadius: 14 }}
      />
      <Names>
        <SymbolLine>
          <SymbolName>{signal.symbol}</SymbolName>
          <SignalFollowAlertIcon signal={signal} />
        </SymbolLine>
        {(side || leverage) && (
          <SubLine>
            {side && (
              <Side
                style={{
                  color: side === Direction.SHORT ? tokens.down : tokens.up,
                }}
              >
                {translateOptionName(side)}
              </Side>
            )}
            {leverage && <span>{leverage}</span>}
          </SubLine>
        )}
      </Names>
    </Pair>
  );
};

export default React.memo(NewLookSignalSymbol);

const Pair = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;
const Names = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;
const SymbolLine = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
`;
const SymbolName = styled.span`
  font-size: 14px;
  line-height: 18px;
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const SubLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const Side = styled.span`
  font-weight: 500;
`;
